import React from 'react';
import ServerError from '../../../../components/ServerError';
import { useQueryParams } from '../../../../hooks/useQueryParams';
import {
  OrganizationLoginOptionType,
  useGetAuthenticationOptionsQuery,
} from '../../__generated__/graphql';
import { useTranslation } from 'react-i18next';
import Loading from './Loading';
import CompanyPicker from './CompanyPicker';
import LoginWithApiToken from './LoginWithApiToken';
import LoginWithSaml from './LoginWithSaml';
import LoginPasswordFormJwt from './LoginPasswordFormJwt';
import LoginWithAuthorizationCode from './LoginWithAuthorizationCode';
import LoginWithUUID from './LoginWithUUID';
import Button from '../../../../components/button';
import { Redirect, useHistory } from 'react-router-dom';
import { routes } from '../../routes';
import { useAuth } from '../../../../context/AuthContext';
import { safeRedirectTo } from '../../../../utils/safeRedirect';
import { isSharedHubDomain } from '../../sharedHubDomain';

const LoginPage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { data, loading, error } = useGetAuthenticationOptionsQuery();
  const { t } = useTranslation();
  const history = useHistory();

  if (loading) {
    return <Loading />;
  }

  if (error || !data) {
    return <ServerError error={error} />;
  }

  if ((data.getAuthenticationOptions?.options ?? []).length > 1) {
    const hasPassword = data.getAuthenticationOptions?.options?.some(
      (option) => option.type === OrganizationLoginOptionType.Password,
    );

    const hasEmail = data.getAuthenticationOptions?.options?.some(
      (option) => option.type === OrganizationLoginOptionType.Email,
    );

    const hasSaml = data.getAuthenticationOptions?.options?.some(
      (option) => option.type === OrganizationLoginOptionType.Saml,
    );

    return (
      <>
        {hasPassword && (
          <div className="mb-3">
            <LoginPasswordFormJwt />
          </div>
        )}

        {hasEmail && (
          <div className="mb-3">
            <Button
              className="w-100"
              label={t('signIn.email.button')}
              onClick={(e) => {
                e.preventDefault();
                history.push(routes.loginWithEmail.route.create({}));
              }}
            />
          </div>
        )}

        {(hasEmail || hasPassword) && hasSaml && (
          <p className="text-muted text-center">{t('signIn.or')}</p>
        )}

        {!hasPassword && !hasEmail && (
          <>
            <h1 className="display-4 text-center mb-3">{t('signIn.title')}</h1>
            <p className="text-muted text-center mb-5">
              {t('signInSaml.multipleProviders')}
            </p>
          </>
        )}

        {data.getAuthenticationOptions?.options?.map((option) => {
          if (option.type === OrganizationLoginOptionType.Saml) {
            return (
              <LoginWithSaml
                key={option.id}
                providerId={option.id}
                providerName={option.name}
                headless
                className="mb-3"
              />
            );
          }
          return null;
        })}
      </>
    );
  }

  if (data.getAuthenticationOptions?.saml) {
    return (
      <LoginWithSaml
        providerId={data.getAuthenticationOptions.saml.id}
        providerName={data.getAuthenticationOptions.saml.name}
      />
    );
  }

  return <LoginPasswordFormJwt />;
};

const Login: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { apiToken, authorizationCode, uuid, redirectTo } = useQueryParams();
  const { isLoggedIn, isLoading } = useAuth();

  // Handle special auth flows (magic links, OAuth, etc.) even if logged in
  if (uuid) {
    return <LoginWithUUID uuid={uuid} />;
  }

  if (authorizationCode) {
    return <LoginWithAuthorizationCode authorizationCode={authorizationCode} />;
  }

  if (apiToken) {
    return <LoginWithApiToken apiToken={apiToken} />;
  }

  // On the shared address, ask which company to go to instead of trying to sign
  // in (which can't disambiguate tenants and would leak across hubs).
  if (isSharedHubDomain()) {
    return <CompanyPicker />;
  }

  // Show loading while checking auth state
  if (isLoading) {
    return <Loading />;
  }

  // Redirect to home/dashboard if already logged in
  if (isLoggedIn) {
    return <Redirect to={safeRedirectTo(redirectTo)} />;
  }

  return <LoginPage />;
};

export default Login;
